/**
 * DomTab — price-ladder Depth of Market with click-to-trade.
 *
 * Honest data policy: live top-of-book (bid/ask + sizes) from the tick
 * stream / level2 endpoint, plus REAL session volume-at-price built from
 * 1-minute bars. No fabricated depth levels — when multi-level SIP/IBKR
 * depth is wired, the ladder consumes it with zero UI change.
 *
 * Interactions (institutional ladder conventions):
 *   - click a price in the BID column  -> BUY  limit at that price
 *   - click a price in the ASK column  -> SELL limit at that price
 *   - MKT BUY / MKT SELL buttons       -> market orders
 *   - ATM toggle attaches a bracket (stop% / target%) server-side
 *   - CENTER recenters the ladder on the current mid
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '@/store';
import { api, HttpError } from '@/lib/api';
import { getUnlockedBrokers, isVaultUnlocked, redirectToVault } from '@/lib/vault';
import { type BrokerName, entryForBroker, isBrokerName, withBroker } from '@/lib/brokerCreds';
import { confirmOrderFor } from '@/lib/orderConfirm';
import { ORG, GRN, MUT, FONT } from '@/theme';

const RED = '#e05252';

interface L2 {
  bids: Array<{ price: number | null; size: number }>;
  asts?: never;
  asks: Array<{ price: number | null; size: number }>;
  // The venue that answered, and the provenance of the numbers. Both are
  // returned by GET /api/level2/:symbol and neither was being read.
  broker?: string;
  feed?: string;
}

// Only Alpaca accepts a bracket on the order itself. dispatch.ts hard-REJECTS
// takeProfit/stopLoss for IBKR and Tradovate with UNSUPPORTED_ORDER_FEATURE
// -- correctly, because silently dropping the protective leg would be worse.
//
// But this checkbox defaulted ON, so every futures order placed from the
// ladder bounced, and the trader had to work out from an error code that an
// unrelated-looking tickbox was the cause. The default now follows the venue
// that actually answered the book.
const BRACKET_CAPABLE = new Set(['alpaca']);
interface Bar { t?: string; time?: string; o?: number; h?: number; l?: number; c?: number; v?: number; open?: number; high?: number; low?: number; close?: number; volume?: number }

function tickSizeFor(px: number): number {
  if (px >= 1000) return 0.25;
  if (px >= 100) return 0.05;
  if (px >= 10) return 0.01;
  return 0.01;
}
const rnd = (x: number, t: number) => Math.round(x / t) * t;
const fmt = (x: number, t: number) => x.toFixed(t < 0.01 ? 4 : t < 0.05 ? 2 : 2);

export function DomTab() {
  const focus = useStore(s => s.focusSymbol);
  const quote = useStore(s => s.quotes[focus]);
  const vaultOk = isVaultUnlocked();

  const [l2, setL2] = useState<L2 | null>(null);
  // Feed health. A frozen ladder must never look like a live one.
  const [feedErr, setFeedErr] = useState<string | null>(null);
  const [lastTick, setLastTick] = useState<number>(0);
  const [vap, setVap] = useState<Map<number, number>>(new Map());
  const [center, setCenter] = useState<number | null>(null);
  const [qty, setQty] = useState<number | ''>(100);
  const [atmOn, setAtmOn] = useState(true);
  const [stopPct, setStopPct] = useState<number | ''>(1.0);
  const [tgtPct, setTgtPct] = useState<number | ''>(2.0);
  const [busy, setBusy] = useState(false);
  // Click-trading is opt-in per session and never persisted. Defaults to
  // OFF so a fresh tab can't fire an order on an accidental click.
  const [armed, setArmed] = useState(false);
  // What arming CONFIRMED: the router's mode for THIS venue at arm time, and
  // for real money the typed LIVE. Armed clicks send this as confirmed_mode;
  // a venue change disarms (the confirmation was about a different account).
  const [armedFor, setArmedFor] = useState<{ venue: BrokerName; confirmedMode: 'live' | 'paper' | 'demo'; label: string } | null>(null);
  // The venue to ask for by name. The first poll for a symbol carries no
  // credential (we do not know the venue and will not guess it); the router
  // routes by symbol and says which venue answered (`broker`, also on a
  // BROKER_NOT_CONNECTED refusal). From then on we ask for that venue by name
  // and api() attaches THAT broker's credential only.
  const [venueHint, setVenueHint] = useState<BrokerName | null>(null);
  const [lastMsg, setLastMsg] = useState('');
  const centeredOnce = useRef(false);

  const mid = quote ? (quote.bid + quote.ask) / 2 || quote.last : null;
  const tick = mid ? tickSizeFor(mid) : 0.01;

  // WHICH VENUE ANSWERED, AND WHAT IT WILL ACCEPT.
  //
  // Read from the ladder response rather than reclassified here. The server
  // already decided the venue with routeBroker(); a second classifier in the
  // browser would be a second thing to get wrong, and the one that drifts is
  // the one that decides whether an order is even accepted.
  const l2Broker = l2?.broker ?? null;
  const bracketOk = l2Broker == null || BRACKET_CAPABLE.has(l2Broker);
  // Effective, not merely checked. Ticking a box the venue rejects is a
  // rejected order, so the box stops applying when it cannot work.
  const atmEffective = atmOn && bracketOk;

  // Top-of-book sizes: poll level2 (1s) — the tick stream carries prices.
  // Disarm on symbol change. Staying armed across a symbol switch means
  // the next stray click fires on an instrument the user never armed.
  // Also DROP THE PREVIOUS SYMBOL'S BOOK. Keeping `l2` across the switch left
  // the old instrument's bid/ask sizes rendered under the new ticker until the
  // first poll returned -- and indefinitely if that poll failed.
  useEffect(() => { setArmed(false); setArmedFor(null); setFeedErr(null); setL2(null); setVenueHint(null); }, [focus]);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const d = await api<L2>(withBroker(`/api/level2/${encodeURIComponent(focus)}`, venueHint));
        if (!stop) {
          setL2(d); setFeedErr(null); setLastTick(Date.now());
          if (isBrokerName(d?.broker) && d.broker !== venueHint) setVenueHint(d.broker);
        }
      } catch (e) {
        const hb = (e as HttpError)?.body?.broker;
        if (!stop && isBrokerName(hb) && hb !== venueHint) setVenueHint(hb);
        // `catch { /* keep last */ }` kept rendering the last order book
        // indefinitely with no indication the feed had died — a trader
        // clicking a price in a frozen ladder is trading on a stale quote.
        // Keep the book (it's better than a blank ladder) but say it's stale.
        if (!stop) setFeedErr((e as Error).message || 'feed unavailable');
      }
    };
    void poll();
    const id = setInterval(poll, 1000);
    return () => { stop = true; clearInterval(id); };
  }, [focus, venueHint]);

  // The venue moved under an armed ladder: the arm confirmation described a
  // different account. Disarm rather than carry it over.
  useEffect(() => {
    if (armedFor && l2Broker && armedFor.venue !== l2Broker) {
      setArmed(false); setArmedFor(null);
      toast('Ladder venue changed -- click-trading DISARMED. Re-arm to confirm the new account.', { icon: '!' });
    }
  }, [l2Broker, armedFor]);

  // Arming is a confirmation in its own right: the router's mode for this
  // venue, read fresh, and a typed LIVE for real money.
  const onArm = async (want: boolean) => {
    if (!want) { setArmed(false); setArmedFor(null); return; }
    if (!isBrokerName(l2Broker)) { toast.error('The ladder has not reported its venue yet -- cannot arm.'); return; }
    const c = await confirmOrderFor(l2Broker,
      `ARM ONE-CLICK TRADING on ${focus} at ${l2Broker.toUpperCase()}.\n` +
      `Every ladder click and MKT button will then send an order with NO further confirmation.`);
    if (!c) return;
    setArmedFor({ venue: l2Broker, confirmedMode: c.confirmedMode, label: c.info.label });
    setArmed(true);
  };

  // Session volume-at-price from real 1-minute bars.
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const d = await api<{ bars?: Bar[] } | Bar[]>(`/api/ohlcv/${encodeURIComponent(focus)}?period=1d&interval=1m`);
        const bars: Bar[] = Array.isArray(d) ? d : (d.bars ?? []);
        const m = new Map<number, number>();
        const t = tickSizeFor(bars.length ? (bars[bars.length - 1].c ?? bars[bars.length - 1].close ?? 100) : 100);
        for (const b of bars) {
          const c = b.c ?? b.close; const v = b.v ?? b.volume;
          if (c == null || v == null) continue;
          const k = rnd(c, t);
          m.set(k, (m.get(k) ?? 0) + v);
        }
        if (!stop) setVap(m);
      } catch { /* VAP optional */ }
    })();
    return () => { stop = true; };
  }, [focus]);

  // Center once per symbol, and re-center if mid drifts off-ladder.
  useEffect(() => { centeredOnce.current = false; setCenter(null); }, [focus]);
  useEffect(() => {
    if (mid && (!centeredOnce.current || (center != null && Math.abs(mid - center) > 14 * tick))) {
      setCenter(rnd(mid, tick));
      centeredOnce.current = true;
    }
  }, [mid, center, tick]);

  const rows = useMemo(() => {
    if (center == null) return [];
    const out: number[] = [];
    for (let i = 12; i >= -12; i--) out.push(+(center + i * tick).toFixed(6));
    return out;
  }, [center, tick]);

  const maxVap = useMemo(() => Math.max(1, ...Array.from(vap.values())), [vap]);

  const place = async (side: 'buy' | 'sell', orderType: 'market' | 'limit', price?: number) => {
    if (!vaultOk) { toast.error('Unlock your broker vault to trade.'); return; }
    // THE VENUE IS THE ONE THE LADDER SHOWS, NAMED EXPLICITLY ON THE ORDER, AND
    // ONLY THAT VENUE'S CREDENTIAL IS SENT. This sent the vault's "active"
    // entry and let the router route by symbol -- so an Alpaca key could be
    // put on an order the router sent to Tradovate.
    if (!isBrokerName(l2Broker)) { toast.error('The ladder has not reported its venue yet -- refusing to guess which account trades.'); return; }
    const venue: BrokerName = l2Broker;
    const sel = entryForBroker(getUnlockedBrokers(), venue);
    if (!sel.entry) {
      toast.error(sel.problem === 'ambiguous'
        ? `Both ${sel.names.join(' and ')} are unlocked for ${venue} -- lock one. Nothing was sent.`
        : `No unlocked ${venue} vault entry -- this symbol trades at ${venue}. Nothing was sent.`);
      return;
    }
    const entry = sel.entry;
    if (typeof qty !== 'number' || qty <= 0) { toast.error('Qty required'); return; }
    if (!Number.isFinite(qty) || qty > 1_000_000) { toast.error('Qty exceeds the per-order ceiling'); return; }
    if (busy) return;
    // Refuse outright while the depth feed is down. A banner is not enough
    // protection on a surface where the whole interaction model is
    // "click the price you see" — the price you see is not current.
    if (feedErr) {
      toast.error('Depth feed is stale — refusing to trade off a frozen ladder.');
      return;
    }

    // The ladder is 25 rows of clickable price cells wired straight to a
    // live order, plus bare MKT BUY / MKT SELL buttons. One stray click
    // anywhere in that grid put on a real position with no confirmation
    // and no undo. Click-trading is legitimately a feature traders want,
    // so it stays — but it must be ARMED deliberately, and while
    // disarmed every order is confirmed.
    let confirmedMode: 'live' | 'paper' | 'demo';
    if (!armed) {
      const px = price != null ? ` @ ${fmt(price, tick)}` : '';
      // Same confirmation as the order ticket (lib/orderConfirm.ts): the
      // router's mode for this venue, read fresh; typed LIVE for real money.
      const c = await confirmOrderFor(venue,
        `${side.toUpperCase()} ${qty} ${focus}\n` +
        `${orderType.toUpperCase()}${px}\n` +
        `Account: ${entry.label} (your own, via vault)\n` +
        (atmEffective ? `Auto-bracket: ON\n` : '') +
        (atmOn && !bracketOk ? `Auto-bracket: NOT SENT - ${l2Broker} does not accept one\n` : '') +
        `\n(Tick ARM CLICK-TRADING above to skip this confirmation.)`);
      if (!c) return;
      confirmedMode = c.confirmedMode;
    } else {
      if (!armedFor || armedFor.venue !== venue) {
        setArmed(false); setArmedFor(null);
        toast.error('Click-trading was armed for a different venue -- DISARMED. Nothing was sent.');
        return;
      }
      confirmedMode = armedFor.confirmedMode;
    }
    // The bracket's TP/SL are computed FROM this reference. `?? 0` meant that
    // with no click price and no mid, the stops were derived from $0 -- either
    // rejected by the venue or, worse, accepted as nonsense levels. If we cannot
    // price the reference we do not guess it.
    const refRaw = price ?? mid;
    if (atmEffective && !(typeof refRaw === 'number' && Number.isFinite(refRaw) && refRaw > 0)) {
      toast.error('No reference price available — refusing to send an auto-bracket whose stops would be derived from $0.');
      return;
    }
    setBusy(true);
    const ref = typeof refRaw === 'number' && Number.isFinite(refRaw) ? refRaw : NaN;
    const dir = side === 'buy' ? 1 : -1;
    // WHOSE ACCOUNT THIS FILLS IN.
    //
    // /api/orders is a POST, and api() only attaches vault credentials to
    // GET/DELETE reads -- so every DOM click placed its order with NO broker
    // credentials and filled in the PLATFORM's shared brokerage account, even
    // with the vault unlocked. The ladder read the book from one account and
    // traded in another, then reported success. Mirrors OrderTicket.onPlace:
    // the key is unwrapped in the browser and sent per request, never persisted
    // server-side. Routing stays server-side (routeBroker by symbol) -- we
    // attach WHOSE account, not WHICH venue. Read at submit time, not render,
    // because the vault may have been locked/unlocked since mount.
    try {
      const r = await api<{ brokerOrderId?: string }>('/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          symbol: focus, qty, side, orderType, tif: 'day',
          limitPrice: orderType === 'limit' ? price : undefined,
          stopLoss: atmEffective && typeof stopPct === 'number' && ref > 0
            ? +(ref * (1 - dir * stopPct / 100)).toFixed(2) : undefined,
          takeProfit: atmEffective && typeof tgtPct === 'number' && ref > 0
            ? +(ref * (1 + dir * tgtPct / 100)).toFixed(2) : undefined,
          broker:            venue,
          broker_api_key:    entry.key,
          broker_api_secret: entry.secret ?? undefined,
          broker_mode:       entry.wireMode,
          confirmed_mode:    confirmedMode,
        }),
      });
      setLastMsg(`${side.toUpperCase()} ${qty} ${focus} ${orderType}${price ? ' @ ' + fmt(price, tick) : ''} ✓ ${r.brokerOrderId ?? ''}`);
      toast.success(`${side.toUpperCase()} ${qty} ${focus}${atmEffective ? ' +bracket' : ''} -- ${entry.label}`);
    } catch (e) {
      const err = e as HttpError;
      // THREE DIFFERENT FACTS, THREE MESSAGES. Collapsing all failures to
      // "REJECTED" is the dangerous case: a transport failure MIGHT have placed
      // a live order, and "rejected" invites an immediate -- doubling -- re-fire.
      if (err.error === 'TRADING_MODE_MISMATCH') {
        // The router wants an explicit LIVE confirmation, or the account's
        // mode changed since arming. Disarm: the arm confirmation is stale.
        setArmed(false); setArmedFor(null);
        setLastMsg('NOT SENT — confirm live trading (re-arm or confirm the order).');
        toast.error(err.message, { duration: 12000 });
      } else if (err.error === 'DUPLICATE_ORDER_SUPPRESSED') {
        setLastMsg('DUPLICATE SUPPRESSED — no new order was placed.');
        toast('Duplicate suppressed — NO new order. The earlier order is the live one.',
              { icon: '⚠', duration: 7000 });
      } else if (typeof err.status !== 'number') {
        // No HTTP response = UNKNOWN, not rejected. The request may have reached
        // the Router and placed a live order; do not imply it failed.
        setLastMsg('UNCONFIRMED — connection failed before we heard back. Check the blotter before retrying.');
        toast('Could not confirm this order — the connection failed before we heard back. It MAY have been placed. Check the blotter before retrying.',
              { icon: '⚠', duration: 12000 });
      } else {
        setLastMsg(`NOT PLACED: ${err.message}`);
        toast.error(err.message);
      }
    } finally { setBusy(false); }
  };

  const cell: React.CSSProperties = {
    fontFamily: "Consolas, monospace", fontSize: 11, padding: '1px 8px',
    whiteSpace: 'nowrap', userSelect: 'none',
  };
  const inp: React.CSSProperties = {
    background: '#111', color: '#ddd', border: '1px solid #333', fontFamily: FONT,
    fontSize: 11, padding: '3px 6px', width: 64, borderRadius: 2,
  };

  const bidPx = quote ? rnd(quote.bid, tick) : null;
  const askPx = quote ? rnd(quote.ask, tick) : null;
  const bidSz = l2?.bids?.[0]?.size ?? null;
  const askSz = l2?.asks?.[0]?.size ?? null;

  return (
    <div style={{ display: 'flex', gap: 18, height: '100%', minHeight: 0 }}>
      {/* Ladder */}
      <div style={{ overflow: 'auto', flex: '0 0 auto' }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: MUT, fontSize: 9, letterSpacing: 1 }}>
              <th style={cell}>BUY</th><th style={cell}>BID</th><th style={cell}>PRICE</th>
              <th style={cell}>ASK</th><th style={cell}>SELL</th><th style={cell}>VOL@PX</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(px => {
              const isBid = bidPx != null && Math.abs(px - bidPx) < tick / 2;
              const isAsk = askPx != null && Math.abs(px - askPx) < tick / 2;
              const isMid = mid != null && Math.abs(px - rnd(mid, tick)) < tick / 2;
              const v = vap.get(+px.toFixed(6)) ?? vap.get(rnd(px, tick)) ?? 0;
              return (
                <tr key={px} style={{ background: isMid ? '#1a1d22' : 'transparent' }}>
                  <td style={{ ...cell, cursor: 'pointer', color: GRN, opacity: busy ? 0.4 : 0.9 }}
                      onClick={() => place('buy', 'limit', px)}
                      title={`Buy ${qty} limit @ ${fmt(px, tick)}`}>{px <= (mid ?? Infinity) ? '◄' : ''}</td>
                  <td style={{ ...cell, color: GRN, textAlign: 'right', minWidth: 52 }}>
                    {isBid && bidSz != null ? bidSz : ''}
                  </td>
                  <td style={{
                    ...cell, textAlign: 'center', minWidth: 74,
                    color: isBid ? GRN : isAsk ? RED : isMid ? '#fff' : '#8a8f98',
                    fontWeight: isBid || isAsk ? 700 : 400,
                  }}>{fmt(px, tick)}</td>
                  <td style={{ ...cell, color: RED, textAlign: 'left', minWidth: 52 }}>
                    {isAsk && askSz != null ? askSz : ''}
                  </td>
                  <td style={{ ...cell, cursor: 'pointer', color: RED, opacity: busy ? 0.4 : 0.9 }}
                      onClick={() => place('sell', 'limit', px)}
                      title={`Sell ${qty} limit @ ${fmt(px, tick)}`}>{px >= (mid ?? 0) ? '►' : ''}</td>
                  <td style={{ ...cell, minWidth: 120 }}>
                    <div style={{
                      height: 8, width: `${Math.min(100, (v / maxVap) * 100)}%`,
                      background: 'color-mix(in srgb, #6bb6ab 45%, transparent)', borderRadius: 1,
                    }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: FONT, fontSize: 11, minWidth: 210 }}>
        <div style={{ color: MUT, fontSize: 9, letterSpacing: 1.5 }}>DOM · {focus}</div>
        {!vaultOk && (
          <div style={{
            border: '1px solid #c08a3e', background: 'rgba(192,138,62,0.10)',
            padding: '4px 6px', fontSize: 9.5, color: '#c08a3e', lineHeight: 1.4,
          }}>
            <strong>VAULT LOCKED.</strong>{' '}
            <span onClick={redirectToVault} style={{ color: '#d4a45a', cursor: 'pointer', textDecoration: 'underline' }}>Unlock</span>
            {' '}your broker to trade — orders only fill in your own account.
          </div>
        )}
        {feedErr && (
          <div style={{
            border: '1px solid ' + RED, background: 'rgba(224,82,82,0.10)',
            padding: '4px 6px', fontSize: 9.5, color: RED, lineHeight: 1.4,
          }}>
            <strong>STALE — FEED DOWN.</strong> Prices below are frozen
            {lastTick ? ` (last update ${Math.round((Date.now() - lastTick) / 1000)}s ago)` : ''}.
            Do not click-trade this ladder.
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ color: MUT }}>QTY</span>
          <input style={inp} type="number" value={qty} onChange={e => setQty(e.target.value === '' ? '' : +e.target.value)} />
          <button style={{ ...inp, width: 'auto', cursor: 'pointer' }} onClick={() => { if (mid) setCenter(rnd(mid, tick)); }}>CENTER</button>
        </div>
        {/* Arm switch. While disarmed (the default) every ladder click and
            market button is confirmed first. Armed is the institutional
            click-to-trade behaviour traders expect — but it is now a
            deliberate, visible choice rather than the silent default. */}
        <label style={{
          display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer',
          color: armed ? RED : MUT,
          border: `1px solid ${armed ? RED : '#333'}`,
          padding: '3px 6px', borderRadius: 2, fontWeight: armed ? 'bold' : 'normal',
        }}>
          <input type="checkbox" checked={armed} onChange={e => { void onArm(e.target.checked); }} style={{ accentColor: RED }} />
          {armed ? `ARMED (${armedFor?.label ?? '?'} @ ${armedFor?.venue ?? '?'}) — CLICKS FIRE INSTANTLY` : 'ARM CLICK-TRADING'}
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button disabled={busy || !vaultOk} style={{ ...inp, width: 90, cursor: (busy || !vaultOk) ? 'not-allowed' : 'pointer', color: GRN, borderColor: GRN, opacity: (busy || !vaultOk) ? 0.4 : 1 }}
                  onClick={() => place('buy', 'market')}>MKT BUY</button>
          <button disabled={busy || !vaultOk} style={{ ...inp, width: 90, cursor: (busy || !vaultOk) ? 'not-allowed' : 'pointer', color: RED, borderColor: RED, opacity: (busy || !vaultOk) ? 0.4 : 1 }}
                  onClick={() => place('sell', 'market')}>MKT SELL</button>
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: MUT, cursor: 'pointer' }}>
          <input type="checkbox" checked={atmEffective} disabled={!bracketOk}
                 onChange={e => setAtmOn(e.target.checked)}
                 title={bracketOk ? undefined : `${l2Broker} does not accept a bracket on the order`}
                 style={{ accentColor: ORG, opacity: bracketOk ? 1 : 0.4 }} />
          ATM bracket (server-held OCO)
        </label>
        {!bracketOk && (
          <span style={{ fontSize: 9, color: '#c08a3e', marginLeft: 6 }}>
            {l2Broker} rejects brackets - order sent without one
          </span>
        )}
        {atmEffective && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: RED }}>STOP%</span>
            <input style={inp} type="number" step="0.1" value={stopPct} onChange={e => setStopPct(e.target.value === '' ? '' : +e.target.value)} />
            <span style={{ color: GRN }}>TGT%</span>
            <input style={inp} type="number" step="0.1" value={tgtPct} onChange={e => setTgtPct(e.target.value === '' ? '' : +e.target.value)} />
          </div>
        )}
        <div style={{ color: MUT, fontSize: 10, maxWidth: 220, lineHeight: 1.5 }}>
          Click BID side to buy / ASK side to sell at that price. Top-of-book live;
          VOL@PX is real session volume from 1-minute bars. Every order is signed to
          the audit chain.
        </div>
        {lastMsg && (
          <div style={{
            color: /^REJECTED/.test(lastMsg) ? RED
                 : /^(DUPLICATE|UNCONFIRMED)/.test(lastMsg) ? '#c08a3e'
                 : GRN,
            fontSize: 10,
          }}>{lastMsg}</div>
        )}
      </div>
    </div>
  );
}
